import { useCoversScreen } from '@sorrem-client/ui/hooks';
import type { ReceivedVision } from '@sorrem/shared';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { VisionPlate } from '../VisionPlate';
import styles from './VisionPlayer.module.scss';

const ADVANCE_KEYS = new Set([' ', 'Enter', 'ArrowRight']);

interface VisionPlayerProps {
    vision: ReceivedVision;
    onFinished: () => void;
}

/**
 * Relives a vision: its plate fills the screen, washed out at the edges as if
 * seen through light, and its text surfaces a paragraph at a time. Tap or
 * press Space for the next; after the last, it fades and ends. Escape ends it
 * early, as the hint says; there is no button.
 */
export function VisionPlayer({ vision, onFinished }: VisionPlayerProps): JSX.Element {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [shownCount, setShownCount] = useState(1);
    useCoversScreen();

    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    const advance = useCallback((): void => {
        if (shownCount >= vision.text.length) {
            onFinished();
        } else {
            setShownCount(shownCount + 1);
        }
    }, [onFinished, shownCount, vision.text.length]);

    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.repeat) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            onFinished();
        } else if (ADVANCE_KEYS.has(event.key) && event.target === event.currentTarget) {
            event.preventDefault();
            advance();
        }
    }, [advance, onFinished]);

    // Rendered into the body, like the cutscene player, so no panel clips it.
    return createPortal(
        <div
            ref={dialogRef}
            className={styles.visionPlayer}
            role="dialog"
            aria-modal="true"
            aria-label={vision.title}
            tabIndex={-1}
            onClick={advance}
            onKeyDown={handleKeyDown}
        >
            <VisionPlate className={styles.plate} vision={vision} />
            <div className={styles.light} aria-hidden="true" />

            <div className={styles.text} aria-live="polite">
                {vision.text.slice(0, shownCount).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>

            <span className={styles.hint}>Tap or press Space to continue - Esc to skip</span>
        </div>,
        document.body,
    );
}
